import { useContext, useEffect, useState } from "react";
import { AppLayout } from "../../../containers/layout/app";
import { TransactionReversalWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import { BaseAlertModal } from "../../../components/modal/alert";
import { AlertModalCheckIcon } from "../../../assets";
import { useNavigate } from "react-router-dom";
import { Box, Card, CardContent, CircularProgress, Grid2, SelectChangeEvent, Stack, Typography } from "@mui/material";
import Cookies from "universal-cookie";
import { useQuery } from "@tanstack/react-query";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseInput } from "../../../components/form/input/styled";
import { BaseButton } from "../../../components/button/styled";
import { formatAmountDisplay } from "../../../helpers/formatAmountDisplay";
import { retrieveUserProfileService } from "../../../utils/api/usermanagement/profile/retrieveUser";
import { retrieveInstitutionByIdService } from "../../../utils/outbound/institution/retrieveInstitutionById";
import { retrieveCurrencyByIdService } from "../../../utils/outbound/currency/retrieveCurrencyById";
import { postTransactionReversalService } from "../../../utils/api/transaction/reversal/postTransactionReversal";
import { retrieveTransactionService } from "../../../utils/api/transaction/retrieveTransaction";
import { formatDate } from "../../../helpers/dateFormatter";
import { retrieveMFIUserService } from "../../../utils/api/usermanagement/mfiuser/retrieveMFIUser";
import { retrieveSavingsAccountByAccountNumberService } from "../../../utils/outbound/savingsaccount/retrieveSavingsAccountByAccountNumber";
import { retrieveCustomerService } from "../../../utils/api/customerservice/customerlist/retrieveCustomer";

export const TransactionReversal = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();
    const { setActiveNavItem } = useContext(AppContext);

    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [isLoadingTransactionDetail, setIsLoadingTransactionDetail] = useState(false);
    const [isPostingTransactionReversal, setIsPostingTransactionReversal] = useState(false);
    const [errorInLoadingTransactionDetail, setErrorInLoadingTransaction] = useState<string | null>(null);
    const [errorInPostingTransactionReversal, setErrorInPostingTransactionReversal] = useState<string | null>(null);
    const [transaction, setTransaction] = useState({} as Record<any, any>);
    const [formDetails, setFormDetails] = useState({
        transactionId: "",
    });

    useEffect(() => {
        setActiveNavItem("Accounting");
    }, [setActiveNavItem]);

    const { data: transactingTeller } = useQuery({
        queryKey: ['transactingTeller', TOKEN],
        queryFn: async () => {
            return await retrieveUserProfileService(TOKEN);
        },
        enabled: !!TOKEN,
    });

    const { data: currency } = useQuery({
        queryKey: ['transactingCurrencySymbol', transactingTeller?._id, transactingTeller?.institution],
        queryFn: async () => {
            if (!transactingTeller) return null;
            const institution = await retrieveInstitutionByIdService(transactingTeller._id, transactingTeller.institution);
            const currencyDetails = await retrieveCurrencyByIdService(transactingTeller._id, institution?.currency);
            return currencyDetails;
        },
        enabled: !!transactingTeller,
    });

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        setIsAlertModalOpen(false);
        return navigate(-1);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleLoadTransaction = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!formDetails?.transactionId) return;
        setErrorInLoadingTransaction(null);
        setTransaction({});
        setIsLoadingTransactionDetail(true);
        try {
            const response = await retrieveTransactionService(TOKEN, formDetails.transactionId);
            if (response.status === "success") {
                if (!["deposit", "withdrawal"].includes(response?.data?.type)) {
                    setErrorInLoadingTransaction("Can only reverse deposit or withdrawal.");
                    setIsLoadingTransactionDetail(false);
                    return;
                };

                if (!["approved"].includes(response?.data?.status)) {
                    setErrorInLoadingTransaction("Can only reverse approved transactions.");
                    setIsLoadingTransactionDetail(false);
                    return;
                };

                const [postedBy, customerSavingsAccount] = await Promise.all([
                    retrieveMFIUserService(TOKEN, response?.data?.initiator),
                    retrieveSavingsAccountByAccountNumberService(TOKEN, response?.data?.bankAccountReference)
                ]);

                const customer = await retrieveCustomerService(TOKEN, customerSavingsAccount.ownerId);

                const transformedResponse = {
                    ...response?.data,
                    initiator: `${postedBy?.firstName ?? ""} ${postedBy?.lastName ?? ""}`,
                    customerName: `${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`
                };

                setTransaction(transformedResponse);
                setIsLoadingTransactionDetail(false);
                return;
            } else {
                setIsLoadingTransactionDetail(false);
                setErrorInLoadingTransaction(`Failed to load transaction. Please check credentials and try again.`);
            }
        } catch (error: any) {
            setIsLoadingTransactionDetail(false);
            setErrorInLoadingTransaction(`Failed to load transaction. ${error.message}`);
            console.error(`Failed to load transaction:`, error);
        }
    }

    const handlePostTransactionReversal = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        setErrorInPostingTransactionReversal(null);
        setIsPostingTransactionReversal(true);
        try {
            const response = await postTransactionReversalService(TOKEN, formDetails);
            if (response.status === "success") {
                setIsPostingTransactionReversal(false);
                setIsAlertModalOpen(true);
            } else {
                setIsPostingTransactionReversal(false);
                setErrorInPostingTransactionReversal(`Failed to post transaction reversal. Please check credentials and try again.`);
            }
        } catch (error: any) {
            setIsPostingTransactionReversal(false);
            setErrorInPostingTransactionReversal(`Failed to post transaction reversal. ${error.message}`);
            console.error(`Failed to post transaction reversal:`, error);
        }
    };

    return (
        <AppLayout
            pageId={"Transaction-Reversal"}
            pageTitle={"Transaction Reversal"}
        >
            <TransactionReversalWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title={"Transaction Reversal Successful"}
                    message="Your transaction reversal has been successfully posted."
                    callToAction="OK"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--text-salmon-pink-color)"
                    >
                        Enter required information to view transaction details.
                    </Typography>
                </Box>
                <Stack
                    direction={{ laptop: "row" }}
                    gap={{ mobile: "var(--flex-gap)", laptop: "calc(var(--flex-gap) * 2)" }}
                    alignItems={{ laptop: "flex-start" }}
                    justifyContent={{ laptop: "space-between" }}
                >
                    <form
                        onSubmit={handleLoadTransaction}
                    >
                        <Grid2
                            container
                            component={"div"}
                            spacing={"var(--flex-gap)"}
                            className="fieldset-grid-wrapper"
                        >
                            <Grid2
                                size={{ mobile: 12 }}
                            >
                                <BaseFieldSet>
                                    <BaseLabel
                                        colour="var(--dark-color)"
                                    >
                                        Enter Transaction ID:
                                    </BaseLabel>
                                    <BaseInput
                                        required
                                        name="transactionId"
                                        value={formDetails.transactionId}
                                        placeholder="Enter Transaction ID"
                                        onChange={(e) => handleChange(e)}
                                    />
                                </BaseFieldSet>
                            </Grid2>
                        </Grid2>
                        {errorInLoadingTransactionDetail && <Typography
                            fontFamily={"Inter"}
                            fontWeight={"600"}
                            fontSize={14}
                            lineHeight={"normal"}
                            color={"var(--text-salmon-pink-color)"}
                            whiteSpace={"normal"}
                        >
                            {errorInLoadingTransactionDetail}
                        </Typography>}
                        <Box
                            overflow={"hidden"}
                        >
                            <BaseButton
                                type="submit"
                                variant="contained"
                                bgcolor="var(--primary-color)"
                                disableElevation
                                sx={{
                                    width: { mobile: "100%", miniTablet: "auto" }
                                }}
                            >
                                {isLoadingTransactionDetail ? (
                                    <CircularProgress color="inherit" className="loader" />
                                ) : (
                                    <Typography
                                        variant={"button"}
                                        fontFamily={"inherit"}
                                        fontWeight={"inherit"}
                                        fontSize={"inherit"}
                                        lineHeight={"inherit"}
                                        color={"inherit"}
                                        textTransform={"inherit"}
                                    >
                                        Load
                                    </Typography>
                                )}
                            </BaseButton>
                        </Box>
                    </form>
                    <Card
                        sx={{
                            borderRadius: "1rem", flex: 0.4
                        }}
                    >
                        <CardContent
                            className="card-content"
                        >
                            <Stack
                                height={"100%"}
                                padding={"var(--basic-padding)"}
                                gap={"var(--flex-gap)"}
                            >
                                <Stack>
                                    <Box>
                                        <Typography
                                            variant="h2"
                                            fontFamily={"Poppins"}
                                            fontWeight={600}
                                            fontSize={20}
                                            lineHeight={"normal"}
                                            color="var(--dark-color)"
                                        >
                                            Status
                                        </Typography>
                                    </Box>
                                    <Box>
                                        {isLoadingTransactionDetail ? (
                                            <CircularProgress color="inherit" className="loader" />
                                        ) : (
                                            <Typography
                                                variant="body1"
                                                fontFamily={"Poppins"}
                                                fontWeight={400}
                                                fontSize={16}
                                                lineHeight={"normal"}
                                                color="var(--primary-color)"
                                            >
                                                {transaction.status}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                                <Stack
                                    gap={"calc(var(--flex-gap)/2)"}
                                >
                                    <Box>
                                        <Typography
                                            variant="h2"
                                            fontFamily={"Poppins"}
                                            fontWeight={600}
                                            fontSize={20}
                                            lineHeight={"normal"}
                                            color="var(--dark-color)"
                                        >
                                            Transaction Details
                                        </Typography>
                                    </Box>
                                    <Stack
                                        gap={"calc(var(--flex-gap)/4)"}
                                    >
                                        <Stack
                                            gap={{
                                                mobile: "calc(var(--flex-gap)/8)",
                                                tablet: "calc(var(--flex-gap)/4)",
                                                laptop: "calc(var(--flex-gap)/8)",
                                                desktop: "calc(var(--flex-gap)/4)"
                                            }}
                                            direction={{ mobile: "column", tablet: "row", laptop: "column", desktop: "row" }}
                                        >
                                            <Box
                                                overflow={"hidden"}
                                            >
                                                <Typography
                                                    variant="h2"
                                                    fontFamily={"Poppins"}
                                                    fontWeight={500}
                                                    fontSize={16}
                                                    lineHeight={"normal"}
                                                    color="var(--dark-color)"
                                                >
                                                    Amount:
                                                </Typography>
                                            </Box>
                                            <Box
                                                overflow={"hidden"}
                                            >
                                                {isLoadingTransactionDetail ? (
                                                    <CircularProgress color="inherit" className="loader" />
                                                ) : (
                                                    <Typography
                                                        variant="h2"
                                                        fontFamily={"Poppins"}
                                                        fontWeight={400}
                                                        fontSize={16}
                                                        lineHeight={"normal"}
                                                        color="var(--dark-color)"
                                                    >
                                                        {currency?.symbol || ""}{formatAmountDisplay(transaction?.amount)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                        <Stack
                                            gap={{
                                                mobile: "calc(var(--flex-gap)/8)",
                                                tablet: "calc(var(--flex-gap)/4)",
                                                laptop: "calc(var(--flex-gap)/8)",
                                                desktop: "calc(var(--flex-gap)/4)"
                                            }}
                                            direction={{ mobile: "column", tablet: "row", laptop: "column", desktop: "row" }}
                                        >
                                            <Box
                                                overflow={"hidden"}
                                            >
                                                <Typography
                                                    variant="h2"
                                                    fontFamily={"Poppins"}
                                                    fontWeight={500}
                                                    fontSize={16}
                                                    lineHeight={"normal"}
                                                    color="var(--dark-color)"
                                                >
                                                    Customer Name:
                                                </Typography>
                                            </Box>
                                            <Box
                                                overflow={"hidden"}
                                            >
                                                {isLoadingTransactionDetail ? (
                                                    <CircularProgress color="inherit" className="loader" />
                                                ) : (
                                                    <Typography
                                                        variant="h2"
                                                        fontFamily={"Poppins"}
                                                        fontWeight={400}
                                                        fontSize={16}
                                                        lineHeight={"normal"}
                                                        color="var(--dark-color)"
                                                    >
                                                        {transaction?.customerName}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                        <Stack
                                            gap={{
                                                mobile: "calc(var(--flex-gap)/8)",
                                                tablet: "calc(var(--flex-gap)/4)",
                                                laptop: "calc(var(--flex-gap)/8)",
                                                desktop: "calc(var(--flex-gap)/4)"
                                            }}
                                            direction={{ mobile: "column", tablet: "row", laptop: "column", desktop: "row" }}
                                        >
                                            <Box
                                                overflow={"hidden"}
                                            >
                                                <Typography
                                                    variant="h2"
                                                    fontFamily={"Poppins"}
                                                    fontWeight={500}
                                                    fontSize={16}
                                                    lineHeight={"normal"}
                                                    color="var(--dark-color)"
                                                >
                                                    Date/Time:
                                                </Typography>
                                            </Box>
                                            <Box
                                                overflow={"hidden"}
                                            >
                                                {isLoadingTransactionDetail ? (
                                                    <CircularProgress color="inherit" className="loader" />
                                                ) : (
                                                    <Typography
                                                        variant="h2"
                                                        fontFamily={"Poppins"}
                                                        fontWeight={400}
                                                        fontSize={16}
                                                        lineHeight={"normal"}
                                                        color="var(--dark-color)"
                                                    >
                                                        {formatDate(transaction?.createdAt, false)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                        <Stack
                                            gap={{
                                                mobile: "calc(var(--flex-gap)/8)",
                                                tablet: "calc(var(--flex-gap)/4)",
                                                laptop: "calc(var(--flex-gap)/8)",
                                                desktop: "calc(var(--flex-gap)/4)"
                                            }}
                                            direction={{ mobile: "column", tablet: "row", laptop: "column", desktop: "row" }}
                                        >
                                            <Box
                                                overflow={"hidden"}
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    fontFamily={"Poppins"}
                                                    fontWeight={500}
                                                    fontSize={16}
                                                    lineHeight={"normal"}
                                                    color="var(--dark-color)"
                                                >
                                                    Posted By:
                                                </Typography>
                                            </Box>
                                            <Box
                                                overflow={"hidden"}
                                            >
                                                {isLoadingTransactionDetail ? (
                                                    <CircularProgress color="inherit" className="loader" />
                                                ) : (
                                                    <Typography
                                                        variant="body1"
                                                        fontFamily={"Poppins"}
                                                        fontWeight={400}
                                                        fontSize={16}
                                                        lineHeight={"normal"}
                                                        color="var(--dark-color)"
                                                    >
                                                        {transaction?.initiator}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    </Stack>
                                </Stack>
                                {errorInPostingTransactionReversal && (
                                    <Box
                                        overflow={"hidden"}
                                    >
                                        <Typography
                                            fontFamily={"Inter"}
                                            fontWeight={"600"}
                                            fontSize={14}
                                            lineHeight={"normal"}
                                            color={"var(--text-salmon-pink-color)"}
                                            whiteSpace={"normal"}
                                        >
                                            {errorInPostingTransactionReversal}
                                        </Typography>
                                    </Box>
                                )}
                                <Box
                                    overflow={"hidden"}
                                >
                                    <BaseButton
                                        type="button"
                                        variant="contained"
                                        bgcolor="var(--primary-color)"
                                        disableElevation
                                        sx={{
                                            width: { mobile: "100%", miniTablet: "auto" }
                                        }}
                                        onClick={handlePostTransactionReversal}
                                    >
                                        {isPostingTransactionReversal ? (
                                            <CircularProgress color="inherit" className="loader" />
                                        ) : (
                                            <Typography
                                                variant={"button"}
                                                fontFamily={"inherit"}
                                                fontWeight={"inherit"}
                                                fontSize={"inherit"}
                                                lineHeight={"inherit"}
                                                color={"inherit"}
                                                textTransform={"inherit"}
                                            >
                                                Reverse
                                            </Typography>
                                        )}
                                    </BaseButton>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            </TransactionReversalWrapper>
        </AppLayout>
    )
}